<script setup lang="ts">
import {trackUser} from './utils/posthog';
import {apiFetch} from './composables/apiFetch';
import type {PlanObject} from './composables/usePlan';

const {$propelAuth} = useNuxtApp();
const colorMode = useColorMode();
if (colorMode?.preference) {
  colorMode.preference = 'light'; // Despite these docs (https://ui.nuxt.com/getting-started/theming#dark-mode), some customers were still getting dark mode if system was set to it
}

const route = useRoute();
if (route?.query?.redirect) {
  await navigateTo(route?.query?.redirect as string, {external: true});
}

useHead({
  meta: [{charset: 'utf-8'}, {name: 'viewport', content: 'width=device-width, initial-scale=1'}],
  link: [{rel: 'icon', href: '/img/favicon.png'}],
  htmlAttrs: {
    lang: 'en',
  },
  script: [
    {src: 'https://js.stripe.com/v3'},
    {
      children: `(function () {
        var e = window;
        var t = document;
        var n = function () {
          n.e(arguments);
        };
        n.q = [];
        n.e = function (e) {
          n.q.push(e);
        };
        e.Pylon = n;
        var r = function () {
          var e = t.createElement("script");
          e.setAttribute("type", "text/javascript");
          e.setAttribute("async", "true");
          e.setAttribute(
            "src",
            "https://widget.usepylon.com/widget/2e6349d2-09e8-49ee-8756-656cf20a7c8c"
          );
          var n = t.getElementsByTagName("script")[0];
          n.parentNode.insertBefore(e, n);
        };
        if (t.readyState === "complete") {
          r();
        } else if (e.addEventListener) {
          e.addEventListener("load", r, false);
        }
      })();`,
    },
    {
      children: `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`,
    },
    {src: 'https://r.wdfl.co/rw.js', async: true, 'data-rewardful': '294b23'},
    {
      children: `!function(d,w){var t="SB87zfHKy67ev9F15y5gDIHR3g1xlXLmsCsZt0MG9TFCi1-1Sb1eB-FbrjybsIg4NveEVU",c="chmln",i=d.createElement("script");if(w[c]||(w[c]={}),!w[c].root){w[c].accountToken=t,w[c].location=w.location.href.toString(),w[c].now=new Date,w[c].fastUrl='https://fast.chameleon.io/';var m="identify alias track clear set show on off custom help _data".split(" ");for(var s=0;s<m.length;s++){!function(){var t=w[c][m[s]+"_a"]=[];w[c][m[s]]=function(){t.push(arguments);};}();}i.src=w[c].fastUrl+"messo/"+t+"/messo.min.js",i.async=!0,d.head.appendChild(i);}}(document,window);`,
    },
  ],
});

const title = 'Vector';
const description = 'Person-level site identity';

useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
});

const auth = await ($propelAuth as any)?.getAuthenticationInfoOrNull();
const plan: PlanObject = await usePlan();

if (auth?.user) {
  trackUser(auth.user.userId, {
    email: auth.user.email,
    name: `${auth.user.firstName} ${auth.user.lastName}`,
    first_name: auth.user.firstName,
    last_name: auth.user.lastName,
    plan: plan.planName,
    is_annual_plan: plan.isAnnualPlan,
    has_paid_plan: plan.hasPaidPlan,
    user_created_at: auth.user.createdAt,
    login_method: auth.user.loginMethod,
  });

  // Chameleon user identification
  try {
    const {getActiveOrg} = await useActiveOrg();
    const activeOrgId = getActiveOrg();

    // Get user hash from backend for security (Chameleon requirement)
    const {data: userHash} = await apiFetch(`/users/${auth.user.userId}/hash`).catch(() => ({
      data: null,
    }));

    (window as any).chmln?.identify(auth.user.userId, {
      uid_hash: userHash, // sha256 hash of user ID for security
      email: auth.user.email,
      name: `${auth.user.firstName} ${auth.user.lastName}`,
      created: auth.user.createdAt, // ISO8601 format
      role: plan.planName, // Using plan as role
      logins: auth.user.loginCount || 0, // Login count if available
      project: activeOrgId, // Current organization ID
      // Additional Vector-specific properties
      login_method: auth.user.loginMethod,
      has_paid_plan: plan.hasPaidPlan,
      is_annual_plan: plan.isAnnualPlan,
    });
  } catch (error) {
    console.warn('Failed to identify user with Chameleon:', error);
  }

  (window as any).pylon = {
    chat_settings: {
      app_id: '2e6349d2-09e8-49ee-8756-656cf20a7c8c',
      email: auth.user.email,
      name: `${auth.user.firstName} ${auth.user.lastName}`,
    },
  };
}
</script>

<template>
  <NuxtLoadingIndicator />
  <ClientOnly>
    <UMain>
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
    </UMain>
    <UNotifications />
    <UModals />
    <USlideovers />
    <ConfirmationModal />
  </ClientOnly>
</template>

<style>
#vbackground {
  background: radial-gradient(circle at top, #3e6aee, #ffffff, #ffffff);
  background-size: 200% 100%;
  background-position: center -300px;
  background-repeat: no-repeat;
}

.PylonChat-bubbleFrameContainer {
  bottom: 32px !important;
}
.PylonChat-chatWindowFrameContainer {
  bottom: 32px !important;
}

/* UTable loading */
tr td.absolute {
  bottom: 0 !important;
  z-index: 1 !important;
}

progress {
  height: 3px !important;
  line-height: 3px !important;
}

progress.bar-animation-carousel::-webkit-progress-bar {
  background-color: transparent !important;
}

progress.bar-animation-carousel::-webkit-progress-bar.dark,
.dark progress.bar-animation-carousel::-webkit-progress-bar {
  background-color: transparent !important; /* or a lighter red if needed */
}

/* Firefox support */
progress.bar-animation-carousel {
  background-color: transparent !important;
}

.dark progress.bar-animation-carousel {
  background-color: transparent !important;
}
</style>
